import React, { useEffect, useState } from "react";
import { Button, Dialog, Label } from "usebeauty";
import classNames from "classnames";
import { useRoutes } from "@router/Router";
import Queue from "@common/queue";
import utils from "@common/utils";
import { useFaceContext } from "@context/FaceContext";
import { useOCRContext } from "@context/OCRContext";
import sdk from "@sdk";
import * as api from "@api";
import { Back, Close } from "@Icons";
import "./FacePage.scss";

const FacePage = () => {
  const { step, navigate } = useRoutes();
  const ocrContext = useOCRContext();
  const faceContext = useFaceContext();
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const loadHandler = async () => {
    return await sdk.face.init();
  };

  const requestHandler = async () => {
    const imageA = ocrContext.croppedImage;
    const livenessImages = faceContext.livenessQueue.takeAll();

    try {
      const livenessImage1 = faceContext.livenessQueue.takeFirst();
      const imageBResp = await api.croppedFace({
        image: livenessImage1,
        resizeWidth: 200,
        ratio: 0.33,
      });

      const imageB = imageBResp?.data.image_data;
      if (!imageB) 
        throw new Error(`얼굴이 인식되지 않습니다. <br /> 다시 시도해 주세요.`); // prettier-ignore

      const { data: liveness } = await api.livenessMultiframeV2Unzip(livenessImages);
      const { data: compare } = await api.compare(imageA, imageB);

      // livenessFailCode: [FAIL-0001, ..., FAIL-0011]
      // compareFailCodeA: [FAIL-A001, ..., FAIL-A006]
      // compareFailCodeB: [FAIL-B001, ..., FAIL-B006]
      const livenessFailCode = Array.from(new Array(12), (_, i) => "FAIL-00" + i.toString().padStart(2, "0"));
      const compareFailCodeA = Array.from(new Array(7), (_, i) => "FAIL-A0" + i.toString().padStart(2, "0"));
      const compareFailCodeB = Array.from(new Array(7), (_, i) => "FAIL-B0" + i.toString().padStart(2, "0"));
      const fail = ([] as string[]).concat(livenessFailCode, compareFailCodeA, compareFailCodeB);
      const failCode = fail.find((el) => el === compare.return_msg.return_code || el === liveness.return_msg.return_code); // prettier-ignore
      if (failCode) 
        throw new Error(`얼굴이 인식되지 않습니다. <br /> 다시 시도해 주세요. <br /> code: ${failCode}`); // prettier-ignore

      faceContext.handlers.setSelfieImage(imageB);
      faceContext.handlers.setResult({ liveness, compare });

      navigate("next");
    } catch (e) {
      if (utils.isFaceServerError(e)) {
        const return_code = e?.response?.data?.return_msg.return_code;
        const return_msg = e?.response?.data?.return_msg.return_msg;
        setError(`${return_code}<br/>${return_msg}<br/><br/>${e?.message}`);
      } else {
        setError((e as Error).message);
      }
    }
  };

  const scanHandler = async () => {
    const isLoaded = sdk.face.isLoaded();

    if (isLoaded) {
      try {
        setLoading(false);
        sdk.face.stop();
        await sdk.face.scan({
          config: faceContext.config,
          onCameraOpened() {
            // console.log("onCameraOpened");
            // setLoading(false);
          },
          onDetected() {
            // console.log("onDetected");
          },
          onProgress() {
            // console.log("onProgress", { value, validation });
          },
          onSuccess(images) {
            console.log({ images });
            const queue = new Queue();
            images.forEach((el) => queue.enqueue(el));
            faceContext.handlers.setLivenessQueue(queue);
            sdk.face.stop();
            setLoading(true);
            setDone(true);
          },
        });
      } catch (e) {
        if (utils.isFaceServerError(e)) {
          const return_code = e?.response?.data?.return_msg.return_code;
          const return_msg = e?.response?.data?.return_msg.return_msg;
          setError(`${return_code}<br/>${return_msg}<br/><br/>${e?.message}`);
        } else {
          setError((e as Error).message);
        }
      }
    }
  };

  const cleanup = () => {
    setLoading(false);
    setDone(false);
    setError("");
    sdk.face.stop();
  };

  const prevHandler = () => {
    sdk.face.stop();
    navigate("prev");
  };

  const modalCloseHandler = () => {
    faceContext.handlers.reset();
    cleanup();
    loadHandler().then(() => scanHandler());
  };

  useEffect(() => {
    faceContext.handlers.reset();
    loadHandler().then(() => scanHandler());
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (done) requestHandler();
  }, [done]);

  return (
    <>
      <Dialog
        open={!!error}
        content={() => {
          return (
            <Dialog.Base id="dialog-face-error" st={{ width: 300 }}>
              <Dialog.Summary closeHandler={modalCloseHandler}></Dialog.Summary>
              <Dialog.Content st={{ height: 220 }}>
                <div dangerouslySetInnerHTML={{ __html: error }}></div>
              </Dialog.Content>
            </Dialog.Base>
          );
        }}></Dialog>
      <div className="alchera-demo-face-scan">
        <div className={classNames("navigation", { loading })}>
          {step.prev && (
            <i onClick={prevHandler}>
              <Back st={{ color: loading ? "#000038" : "#fff", fontSize: "16px" }} />
            </i>
          )}
          <Label text={step.label}></Label>
          <i onClick={() => window.location.reload()}>
            <Close st={{ color: loading ? "#000038" : "#fff", fontSize: "16px" }} />
          </i>
        </div>

        <div className="container">
          {loading && (
            <div className="alchera-demo-loading">
              <div className="spinner">
                {Array.from(new Array(12)).map((_, i) => (
                  <div key={i}></div>
                ))}
              </div>
            </div>
          )}

          {/* FACE SDK REQUIRED ELEMENT */}
          <div data-alchera-face="face" style={{ display: loading ? "none" : "block" }}></div>

          {!loading && !done && (
            <div className="guide">
              <p className="text">얼굴을 정면에서 정확히 인식해 주세요</p>
            </div>
          )}
        </div>

        <div className="footer">
          <div className="steps">
            <span>Step 3</span>
            <div className="step">
              <div className="level prev"></div>
              <div className="level prev"></div>
              <div className="level current"></div>
              <div className="level"></div>
            </div>
          </div>
          <Button ripple={false}>인식 중</Button>
        </div>
      </div>
    </>
  );
};

export default FacePage;
